import { type FirebaseApp, initializeApp } from "firebase/app";
import { GoogleAuthProvider, getAuth, signInWithPopup } from "firebase/auth";

/**
 * Pick a build-time env value, falling back when it is unset OR empty. CI
 * injects these via `VITE_FIREBASE_*` build args; an unprovided GitHub var
 * expands to an empty string, which must still fall back to the default (a
 * plain `??` would keep the empty string and break Firebase init).
 */
const envOr = (value: string | undefined, fallback: string): string =>
  value && value.length > 0 ? value : fallback;

/**
 * Firebase web config for the shared **dchain-demo** project. These values are
 * public by design (they ship in the client bundle), so they live here as
 * defaults and only need overriding to point at a different project. Each is
 * overridable via the matching `VITE_FIREBASE_*` build env (Dockerfile ARG →
 * injected from CI; see deployments/apps/web/Dockerfile).
 */
const firebaseConfig = {
  apiKey: envOr(import.meta.env.VITE_FIREBASE_API_KEY, "AIzaSyDH3UGverRbHHgQbzIOxTTSlgBY6ZUheYc"),
  authDomain: envOr(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN, "dchain-demo.firebaseapp.com"),
  projectId: envOr(import.meta.env.VITE_FIREBASE_PROJECT_ID, "dchain-demo"),
  storageBucket: envOr(
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    "dchain-demo.firebasestorage.app"
  ),
  messagingSenderId: envOr(import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID, "861324849456"),
  appId: envOr(import.meta.env.VITE_FIREBASE_APP_ID, "1:861324849456:web:54a836f34b6fd0c370d8f5"),
};

/**
 * Whether to offer Google sign-in. Enabled by default; set
 * `VITE_FIREBASE_ENABLED=false` for offline local dev to fall back to the
 * email/password form (the API's /auth/google then returns 503 anyway).
 */
export const isFirebaseEnabled = import.meta.env.VITE_FIREBASE_ENABLED !== "false";

let app: FirebaseApp | undefined;

function getFirebaseApp(): FirebaseApp {
  if (!app) app = initializeApp(firebaseConfig);
  return app;
}

/** Open the Google popup and return the signed-in user's Firebase ID token. */
export async function signInWithGoogle(): Promise<string> {
  const auth = getAuth(getFirebaseApp());
  const result = await signInWithPopup(auth, new GoogleAuthProvider());
  return result.user.getIdToken();
}
